/**
 * Desk presets — analyst-focused configuration presets that control
 * which layers, panels, and feed categories are active.
 *
 * Replaces the old variant system with OSINT-focused presets.
 * Analysts pick a preset on first launch, then customize from there.
 */

export interface DeskPreset {
  id: string;
  name: string;
  icon: string;
  description: string;
  /** Layers enabled by default when this desk is activated. */
  defaultLayers: string[];
  /** Panels that should be mounted (lazy-instantiated) for this desk. */
  enabledPanels: string[];
  /** Feed categories to show in the news feed. */
  feedCategories: string[];
  /** Layer group ordering in the layer control panel. */
  layerGroupOrder: string[];
}

/** Built-in desk presets. */
export const DESK_PRESETS: DeskPreset[] = [
  {
    id: 'general',
    name: 'General OSINT',
    icon: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="8" cy="8" r="6.5"/><ellipse cx="8" cy="8" rx="3" ry="6.5"/><line x1="1.5" y1="8" x2="14.5" y2="8"/></svg>',
    description: 'Conflicts, borders, natural disasters, global news',
    defaultLayers: [
      // WM desktop full-variant defaults (variants/full.ts DEFAULT_MAP_LAYERS,
      // catalog section 4): conflicts, bases, hotspots, nuclear, sanctions,
      // weather, waterways, outages, iranAttacks — mapped to Sentinel ids.
      // Sentinel splits WM's conflicts/sanctions toggles into marker + wash
      // layers, so both halves of each stay on.
      'conflicts', 'conflict-zones',
      'hotspots', 'military-bases', 'nuclear',
      'sanctions', 'sanctions-markers',
      'weather-alerts', 'waterways', 'internet-outages', 'iran-attacks',
      // WM renders news locations UNCONDITIONALLY (no layer toggle,
      // DeckGLMap.ts:2304) — the default WM look includes them.
      'news-markers',
      // Everything else is opt-in via the layer panel: WM defaults it off
      // (economic, military, natural/gdacs, earthquakes, fires, gpsJamming,
      // cyberThreats, displacement) or has no equivalent overlay (borders —
      // the basemap carries ordinary country borders; Sentinel's layer is
      // the disputed-territories overlay).
    ],
    enabledPanels: [
      'news-feed', 'insights', 'cii-dashboard', 'risk', 'country',
      'telegram', 'advisory', 'oref', 'ucdp', 'displacement',
      'strategic-posture', 'cascade', 'cyber', 'gpsjam',
      'supply-chain', 'climate', 'trade-policy', 'prediction',
      'country-timeline', 'world-clock',
      'detail', 'layer-control', 'settings', 'service-status',
      'monitor', 'deduction', 'verification', 'signal-detail',
      'gdelt-intel',
    ],
    feedCategories: [
      'breaking', 'conflicts', 'diplomacy', 'military', 'energy', 'climate',
      'health', 'maritime', 'space', 'analysis', 'regional-mena',
      'regional-europe', 'regional-asia', 'regional-africa',
      'regional-americas', 'nuclear', 'sanctions',
    ],
    layerGroupOrder: ['conflicts', 'environmental', 'infrastructure', 'maritime', 'aviation', 'economic', 'borders'],
  },
  {
    id: 'maritime',
    name: 'Maritime',
    icon: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="8" cy="3.5" r="2"/><line x1="8" y1="5.5" x2="8" y2="14"/><path d="M3 11c0 2.8 2.2 3 5 3s5-.2 5-3"/><line x1="5" y1="8" x2="11" y2="8"/></svg>',
    description: 'Vessels, shipping lanes, AIS, ports, GPS jamming',
    defaultLayers: [
      // Core maritime: vessels, ports, shipping, naval
      'vessels', 'shipping-lanes', 'ports', 'chokepoints', 'naval',
      // Infrastructure: cables, GPS jamming, AIS disruption, dark ships
      'undersea-cables', 'cable-advisories', 'gps-jamming', 'ais-disruption', 'dark-ships',
      // Context: sanctions, weather, outages, borders, waterways
      'sanctions', 'sanctions-markers', 'weather-alerts', 'internet-outages', 'borders', 'waterways',
    ],
    enabledPanels: [
      'news-feed', 'insights', 'gpsjam', 'supply-chain', 'service-status',
      'country', 'risk',
      'detail', 'layer-control', 'settings',
      'monitor', 'verification', 'signal-detail',
    ],
    feedCategories: [
      'breaking', 'maritime', 'sanctions', 'energy', 'military',
    ],
    layerGroupOrder: ['maritime', 'infrastructure', 'conflicts', 'environmental', 'economic', 'aviation', 'borders'],
  },
  {
    id: 'cyber',
    name: 'Cyber',
    icon: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M8 1.5L2.5 4v4c0 3.5 2.3 5.5 5.5 7 3.2-1.5 5.5-3.5 5.5-7V4Z"/><path d="M6 8l1.5 1.5L10.5 6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    description: 'Datacenters, APT groups, cyber incidents, internet exchanges',
    defaultLayers: [
      // WM tech variant defaults: cables, outages, datacenters, natural, startupHubs, cloudRegions, techHQs
      // Mapped to Sentinel equivalents + cyber-specific additions
      'datacenters', 'undersea-cables', 'internet-outages', 'gdacs',
      // Sentinel cyber-specific: APT groups, cyber incidents, GPS jamming, cable health
      'cyber-incidents', 'cyber-threats', 'apt-groups', 'gps-jamming',
      'cable-advisories', 'sanctions', 'sanctions-markers',
    ],
    enabledPanels: [
      'news-feed', 'insights', 'cyber', 'gpsjam',
      'service-status', 'country', 'risk',
      'detail', 'layer-control', 'settings',
      'monitor', 'verification', 'signal-detail',
    ],
    feedCategories: [
      'ai-ml', 'cybersecurity', 'cloud', 'hardware', 'regulation',
      'cyber-intel',
    ],
    layerGroupOrder: ['infrastructure', 'conflicts', 'environmental', 'maritime', 'economic', 'aviation', 'borders'],
  },
  {
    id: 'conflict',
    name: 'Conflict',
    icon: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="8" cy="8" r="5"/><circle cx="8" cy="8" r="2"/><line x1="8" y1="1" x2="8" y2="4"/><line x1="8" y1="12" x2="8" y2="15"/><line x1="1" y1="8" x2="4" y2="8"/><line x1="12" y1="8" x2="15" y2="8"/></svg>',
    description: 'Conflicts, military bases, protests, displacement',
    defaultLayers: [
      // Core conflict: conflicts, bases, nuclear, hotspots, UCDP events
      'conflicts', 'military-bases', 'nuclear', 'hotspots', 'ucdp-events',
      // Human impact: protests, displacement, refugee camps
      'protests', 'displacement', 'refugee-camps',
      // Context: fires, natural disasters, sanctions, weather, borders, iran tracking
      'fires', 'gdacs', 'sanctions', 'sanctions-markers', 'weather-alerts', 'borders', 'iran-attacks',
      // Sentinel additions: news markers, military activity, infra disruption
      'news-markers', 'military-activity', 'internet-outages',
    ],
    enabledPanels: [
      'news-feed', 'insights', 'cii-dashboard', 'risk', 'country',
      'telegram', 'oref', 'ucdp', 'displacement', 'advisory',
      'strategic-posture', 'cascade',
      'detail', 'layer-control', 'settings', 'service-status',
      'monitor', 'deduction', 'verification', 'signal-detail',
      'gdelt-intel',
    ],
    feedCategories: [
      'breaking', 'conflicts', 'military', 'diplomacy', 'nuclear',
      'regional-mena', 'regional-europe', 'regional-asia',
      'regional-africa', 'regional-americas',
    ],
    layerGroupOrder: ['conflicts', 'environmental', 'borders', 'infrastructure', 'maritime', 'aviation', 'economic'],
  },
  {
    id: 'economic',
    name: 'Economic',
    icon: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="2,12 5.5,7 9,9.5 14,3"/><line x1="2" y1="14" x2="14" y2="14"/><line x1="2" y1="2" x2="2" y2="14"/></svg>',
    description: 'Sanctions, trade routes, financial centers, minerals',
    defaultLayers: [
      // WM finance variant defaults: stockExchanges, financialCenters, centralBanks,
      // tradeRoutes, cables, pipelines, sanctions, weather, economic, waterways, outages, natural
      'stock-exchanges', 'financial-centers', 'central-banks', 'commodity-hubs',
      'trade-routes', 'undersea-cables', 'pipelines-oil', 'pipelines-gas',
      'sanctions', 'sanctions-markers', 'weather-alerts', 'economic-centers', 'waterways',
      'internet-outages', 'gdacs',
      // Sentinel additions: minerals, borders, news markers
      'critical-minerals', 'borders', 'news-markers',
    ],
    enabledPanels: [
      'news-feed', 'insights', 'market', 'economic', 'macro-signals', 'prediction',
      'trade-policy', 'risk', 'country',
      'detail', 'layer-control', 'settings', 'service-status',
      'monitor', 'signal-detail',
    ],
    feedCategories: [
      'markets', 'central-banks', 'trade-policy', 'commodities', 'crypto',
      'macro', 'sanctions', 'breaking',
    ],
    layerGroupOrder: ['economic', 'conflicts', 'infrastructure', 'maritime', 'environmental', 'aviation', 'borders'],
  },
  {
    id: 'ew',
    name: 'Electronic Warfare',
    icon: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 8h2l1.5-4 2 8 2-6 1.5 4H13"/><circle cx="14" cy="8" r="1"/></svg>',
    description: 'GPS jamming, AIS spoofing, spectrum interference, NOTAM closures',
    defaultLayers: [
      // Core EW: GPS jamming, military aircraft, NOTAM closures
      'gps-jamming', 'military-aircraft', 'notam-closures',
      // Maritime EW: vessels, AIS disruption, dark ships
      'vessels', 'ais-disruption', 'dark-ships',
      // Infrastructure: cables, outages, cyber
      'undersea-cables', 'cable-advisories', 'internet-outages', 'cyber-incidents',
      // Context: military bases, conflicts, borders, news
      'military-bases', 'conflicts', 'borders', 'news-markers',
    ],
    enabledPanels: [
      'news-feed', 'insights', 'gpsjam', 'cyber', 'telegram',
      'country', 'risk', 'advisory', 'strategic-posture',
      'detail', 'layer-control', 'settings', 'service-status',
      'monitor', 'verification', 'signal-detail',
    ],
    feedCategories: [
      'breaking', 'military', 'conflicts', 'cybersecurity',
      'regional-europe', 'regional-mena', 'regional-asia',
      'maritime', 'space', 'nuclear',
    ],
    layerGroupOrder: ['infrastructure', 'aviation', 'maritime', 'conflicts', 'environmental', 'economic', 'borders'],
  },
];

/** Map of desk preset ID -> preset definition. */
export const DESK_MAP: Record<string, DeskPreset> = Object.fromEntries(
  DESK_PRESETS.map(p => [p.id, p]),
);

/** Get a desk preset by ID. Returns undefined if not found. */
export function getDeskById(id: string): DeskPreset | undefined {
  return DESK_MAP[id];
}

/** Get the default desk preset ID. */
export function getDefaultDeskId(): string {
  return 'general';
}

/** Get all desk preset IDs. */
export function getDeskIds(): string[] {
  return DESK_PRESETS.map(p => p.id);
}
